import { EmailProvider } from "@/types/inboxPlacements.types";

type RecommendationType = "hit-spam-folder" | "missing-list-unsubscribe-header";

type Recommendation = {
  type: RecommendationType;
  provider: EmailProvider;
  title: string;
  iconUrl: string;
  links: { label: string; href: string }[];
  content: () => React.ReactNode;
};

export const recommendationList: Recommendation[] = [
  // B2B
  {
    type: "hit-spam-folder",
    provider: EmailProvider.GOOGLEWORKSPACE,
    title: "Your email landed in the Google Workspace spam folder.",
    iconUrl: "/providers-logos/google-workspace.png",
    links: [
      // {
      //   label: "How to Avoid the Google Workspace Spam Folder with InboxDoctor",
      //   href: "#",
      // },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            When a message is flagged by Google’s spam filters, it is diverted
            to the recipient’s <em>Spam</em> folder. This hurts deliverability,
            lowers the chance your email will be read, and can undermine
            communication or marketing efforts.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Monitor Sender Reputation</h4>
          <p>
            Use InboxDoctor’s Postmaster feature to connect to Google Postmaster
            Tools for real-time metrics:
          </p>
          <ul>
            <li>
              <strong>Spam Rate Tracker:</strong> Check the percentage of emails
              marked as spam by recipients compared to inbox deliveries for
              DKIM‑authenticated emails to assess sender trust.
            </li>
            <li>
              <strong>Domain &amp; IP Health Check:</strong> Evaluate whether
              your domain or IP is flagged by Google Workspace, using
              color‑coded indicators to identify potential issues.
            </li>
            <li>
              <strong>Authentication Insights:</strong> Monitor pass rates for
              SPF, DKIM, and DMARC to ensure your emails are properly
              authenticated.
            </li>
            <li>
              <strong>Encryption Monitor:</strong> Compare TLS‑encrypted email
              traffic to total mail volume with dual‑graph visuals to verify
              secure transmission.
            </li>
            <li>
              <strong>Delivery Errors Analyzer:</strong> Identify rejected or
              temporarily failed emails and their reasons in a single graph to
              address delivery issues.
            </li>
          </ul>

          <h4>Enhance Email Authentication</h4>
          <ul>
            <li>Configure &amp; validate SPF, DKIM, DMARC records.</li>
            <li>
              Begin with <code>p=none</code> DMARC, then move to{" "}
              <code>p=quarantine</code>/<code>reject</code> as reputation
              improves.
            </li>
          </ul>

          <h4>Enable TLS Encryption</h4>
          <ul>
            <li>Support TLS on inbound &amp; outbound mail.</li>
            <li>
              Track encryption metrics in InboxDoctor to confirm 100% TLS.
            </li>
          </ul>

          <h4>Report Issues</h4>
          <p>
            Submit delivery problems directly to Google Workspace through
            InboxDoctor’s Postmaster feature using the official{" "}
            <a
              href="https://support.google.com/mail/contact/gmail_bulk_sender_escalation?rd=2&visit_id=638175735740963802-224206049"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Sender Contact Form
            </a>
            .
          </p>

          {/* <h4>Learn More</h4>
          <p>
            Google Workspace official best practices via InboxDoctor’s Best
            Practices Guide for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },
  {
    type: "missing-list-unsubscribe-header",
    provider: EmailProvider.GOOGLEWORKSPACE,
    title: "Missing 'List-Unsubscribe' Header",
    iconUrl: "/providers-logos/google-workspace.png",
    links: [],
    content: () => {
      return <div className="space-y-2"></div>;
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.ZOHO,
    title: "Your email landed in the Zoho Spam folder.",
    iconUrl: "/providers-logos/zoho-mail.png",
    links: [],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            Zoho Workmail employs advanced spam filters to protect users from
            unwanted emails (spam or phishing). Legitimate messages can still be
            flagged if the sender lacks proper authentication (SPF, DKIM,
            DMARC), has a poor reputation, or the content triggers spam filters.
            Additional risk factors include emailing unengaged recipients, high
            bounce rates, or non‑compliance with Zoho’s spam control
            guidelines—such as omitting an unsubscribe link.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Ensure Authentication</h4>

          <p>
            Zoho Mail checks <strong>SPF, DKIM, and DMARC</strong> during
            filtering. Correct setup (per{" "}
            <em>Spam Control – Guidelines and Best Practices</em>) greatly
            reduces spam flags. Use InboxDoctor’s{" "}
            <em>Inbox Placement Test Report</em> to confirm your records.
          </p>

          <h4>Monitor and Improve Sender Reputation</h4>

          <p>
            Routinely review your domain and IP reputation to ensure they are
            not block‑listed or linked to spam activity.
          </p>

          <h4>Follow Zoho’s Spam Control Guidelines</h4>
          <p>
            Zoho lets admins fine‑tune spam filters, adding domains or addresses
            to blocklists/allowlists via <em>Spam Verification Settings</em>.
            Align with these settings to avoid false positives.
          </p>

          <h4>Address Non‑Compliance</h4>
          <p>
            Missing unsubscribe links, emailing unverified recipients, or other
            guideline breaches can trigger filtering—ensure full compliance.
          </p>

          <h4>Report Issues</h4>
          <p>
            Persisting problems? Reach Zoho support at{" "}
            <a
              href="https://www.zoho.com/workplace/contact.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Zoho Workplace Contact Form
            </a>
            .
          </p>
        </div>
      );
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.MICROSOFT365,
    title: "Your email landed in the Microsoft 365 Spam folder",
    iconUrl: "/providers-logos/microsoft-365.png",
    links: [
      // {
      //   label: "How to Avoid the Microsoft 365 Spam Folder with InboxDoctor",
      //   href: "#",
      // },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            When an email lands in the Microsoft 365 Spam folder, it indicates
            that Microsoft 365’s Exchange Online Protection (EOP) filters have
            flagged it as potentially unwanted, preventing it from reaching the
            inbox. This can stem from issues like poor sender reputation,
            inadequate email authentication, high complaint or bounce rates, or
            content triggering spam filters. This can disrupt communication and
            reduce engagement, making it critical to address for effective email
            delivery.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Check Authentication:</h4>
          <p>
            Use InboxDoctor’s Inbox Placment Report to verify SPF, DKIM, and
            DMARC configurations. Microsoft 365 relies on these authentication
            protocols to validate sender legitimacy. Misconfigurations can lead
            to emails being flagged as spam.
          </p>

          <h4>Sign Up for Microsoft’s Smart Network Data Services (SNDS):</h4>
          <p>
            If using a dedicated IP address, enroll in SNDS to access data on
            traffic from your IP, including filtering status, complaint rate,
            and spam trap hits. This helps assess and improve your reputation
            with Microsoft. High complaint or spam trap hits suggest reviewing
            email list sources and ensuring content relevance for subscribed
            recipients.{" "}
            <a href="https://sendersupport.olc.protection.outlook.com/snds/">
              SNDS Link
            </a>
          </p>

          <h4>Check and Improve IP Reputation:</h4>
          <p>
            If using a dedicated IP, review your current IP reputation data and
            request removal from blocklists if listed. Microsoft 365 relies on
            Symantec/Brightmail Probe Network and EOP for filtering, and new IPs
            often lack established reputation, increasing deliverability
            challenges.
          </p>

          <h4>Change the &quot;From&quot; Email Address:</h4>
          <p>
            Switching to a different email address (even within the same domain)
            can sometimes bypass filtering issues and improve inbox delivery.
          </p>

          <h4>Request Whitelisting:</h4>
          <p>
            Contact Microsoft to request your domain be added to their
            whitelist, which can help ensure emails reach the inbox rather than
            the spam folder.{" "}
            <a href="https://support.microsoft.com/en-us/getsupport?oaspworkflow=start_1.0.0.0&wfname=capsub&productkey=edfsmsbl3&ccsid=635688189955348624&wa=wsignin1.0">
              Request Whitelisting
            </a>
          </p>

          {/* <h4>Learn More</h4>
          <p>
            Microsoft 365 official best practices via InboxDoctor’s Best
            Practices Guide for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.WORKMAIL,
    title: "Your email landed in the Amazon WorkMail Spam folder",
    iconUrl: "/providers-logos/aws-workmail.png",
    links: [
      {
        label: "Managing email flows in Amazon WorkMail",
        href: "https://docs.aws.amazon.com/workmail/latest/userguide/email-flows.html",
      },
      {
        label: "Amazon WorkMail FAQs",
        href: "https://aws.amazon.com/workmail/faqs/",
      },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            Amazon WorkMail leverages Amazon Simple Email Service (SES) for
            advanced spam filtering. Legitimate messages can still be flagged if
            the sender lacks proper authentication (SPF, DKIM, DMARC), has a
            poor reputation, or the content triggers spam filters. Additional
            factors include emailing unengaged recipients, high bounce rates, or
            failing to meet Amazon SES deliverability standards.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Ensure Authentication</h4>
          <p>
            Verify <strong>SPF, DKIM, and DMARC</strong> via Amazon SES (see
            <em>Email Deliverability</em>)—WorkMail relies heavily on correct
            authentication for trusted delivery.
          </p>

          <h4>Manage Sending Quotas &amp; Limits</h4>
          <p>
            Confirm your Amazon SES sending quotas match your volume. New
            accounts start with low limits; request increases through an
            <em>SES Sending Limits Increase</em> case.
          </p>

          <h4>Monitor Reputation &amp; Blocklists</h4>
          <p>
            If your sending IP or domain is block‑listed, WorkMail may filter
            your mail. Check status with InboxDoctor’s{" "}
            <em>Inbox Placement Test</em>.
          </p>
        </div>
      );
    },
  },
  // B2C
  {
    type: "hit-spam-folder",
    provider: EmailProvider.OUTLOOK,
    title: "Your email landed in the Outlook Mail spam folder",
    iconUrl: "/providers-logos/outlook.png",
    links: [
      // {
      //   label: "How to Avoid the Outlook Mail Spam Folder with InboxDoctor",
      //   href: "#",
      // },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            When an email lands in the Outlook spam folder, it indicates that
            Microsoft 365’s Exchange Online Protection (EOP) filters have
            flagged it as potentially unwanted, preventing it from reaching the
            inbox. This can stem from issues like poor sender reputation,
            inadequate email authentication, high complaint or bounce rates, or
            content triggering spam filters. This can disrupt communication and
            reduce engagement, making it critical to address for effective email
            delivery.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Check Authentication:</h4>
          <p>
            Use InboxDoctor’s Inbox Placment Report to verify SPF, DKIM, and
            DMARC configurations. Microsoft 365 relies on these authentication
            protocols to validate sender legitimacy. Misconfigurations can lead
            to emails being flagged as spam.
          </p>

          <h4>Sign Up for Microsoft’s Smart Network Data Services (SNDS):</h4>
          <p>
            If using a dedicated IP address, enroll in SNDS to access data on
            traffic from your IP, including filtering status, complaint rate,
            and spam trap hits. This helps assess and improve your reputation
            with Microsoft. High complaint or spam trap hits suggest reviewing
            email list sources and ensuring content relevance for subscribed
            recipients.{" "}
            <a href="https://sendersupport.olc.protection.outlook.com/snds/">
              SNDS Link
            </a>
          </p>

          <h4>Check and Improve IP Reputation:</h4>
          <p>
            If using a dedicated IP, review your current IP reputation data and
            request removal from blocklists if listed. Outlook relies on
            Symantec/Brightmail Probe Network and EOP for filtering, and new IPs
            often lack established reputation, increasing deliverability
            challenges.
          </p>

          <h4>Change the &quot;From&quot; Email Address:</h4>
          <p>
            Switching to a different email address (even within the same domain)
            can sometimes bypass filtering issues and improve inbox delivery.
          </p>

          <h4>Request Whitelisting:</h4>
          <p>
            Contact Microsoft to request your domain be added to their
            whitelist, which can help ensure emails reach the inbox rather than
            the spam folder.{" "}
            <a href="https://support.microsoft.com/en-us/getsupport?oaspworkflow=start_1.0.0.0&wfname=capsub&productkey=edfsmsbl3&ccsid=635688189955348624&wa=wsignin1.0">
              Request Whitelisting
            </a>
          </p>

          {/* <h4>Learn More</h4>
          <p>
            Outlook official best practices via InboxDoctor’s Best Practices
            Guide for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.GMAIL,
    title: "Your email landed in the Gmail Spam folder",
    iconUrl: "/providers-logos/gmail.png",
    links: [
      // {
      //   label: "How to Avoid the Gmail Spam Folder with InboxDoctor",
      //   href: "#",
      // },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            When a message is flagged by Google’s spam filters, it is diverted
            to the recipient’s <em>Spam</em> folder. This hurts deliverability,
            lowers the chance your email will be read, and can undermine
            communication or marketing efforts.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Monitor Sender Reputation</h4>
          <p>
            Use InboxDoctor’s Postmaster feature to connect to Google Postmaster
            Tools for real-time metrics:
          </p>
          <ul>
            <li>
              <strong>Spam Rate Tracker:</strong> Check the percentage of emails
              marked as spam by recipients compared to inbox deliveries for
              DKIM‑authenticated emails to assess sender trust.
            </li>
            <li>
              <strong>Domain &amp; IP Health Check:</strong> Evaluate whether
              your domain or IP is flagged by Gmail, using color‑coded
              indicators to identify potential issues.
            </li>
            <li>
              <strong>Authentication Insights:</strong> Monitor pass rates for
              SPF, DKIM, and DMARC to ensure your emails are properly
              authenticated.
            </li>
            <li>
              <strong>Encryption Monitor:</strong> Compare TLS‑encrypted email
              traffic to total mail volume with dual‑graph visuals to verify
              secure transmission.
            </li>
            <li>
              <strong>Delivery Errors Analyzer:</strong> Identify rejected or
              temporarily failed emails and their reasons in a single graph to
              address delivery issues.
            </li>
          </ul>

          <h4>Enhance Email Authentication</h4>
          <ul>
            <li>Configure &amp; validate SPF, DKIM, DMARC records.</li>
            <li>
              Begin with <code>p=none</code> DMARC, then move to{" "}
              <code>p=quarantine</code>/<code>reject</code> as reputation
              improves.
            </li>
          </ul>

          <h4>Enable TLS Encryption</h4>
          <ul>
            <li>Support TLS on inbound &amp; outbound mail.</li>
            <li>
              Track encryption metrics in InboxDoctor to confirm 100% TLS.
            </li>
          </ul>

          <h4>Report Issues</h4>
          <p>
            Submit delivery problems directly to Gmail through InboxDoctor’s
            Postmaster feature using the official{" "}
            <a
              href="https://support.google.com/mail/contact/gmail_bulk_sender_escalation?rd=2&visit_id=638175735740963802-224206049"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Sender Contact Form
            </a>
            .
          </p>

          {/* <h4>Learn More</h4>
          <p>
            Gmail official best practices via InboxDoctor’s Best Practices Guide
            for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.AOL,
    title: "Your email landed in the AOL Spam folder",
    iconUrl: "/providers-logos/aol.png",
    links: [
      // {
      //   label: "How to Avoid the AOL Spam Folder with InboxDoctor",
      //   href: "#",
      // },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            AOL tracks each sender’s IP and labels its reputation as
            <strong> good, neutral, or bad</strong>. Landing in the{" "}
            <em>neutral</em>
            bucket is usually fine, but a <em>bad</em> reputation pushes your
            messages to spam—or blocks them entirely.
          </p>

          <h3>Troubleshooting Tips</h3>
          <ul>
            <li>
              Double‑check your authentication records—AOL leans hard on{" "}
              <strong>DKIM</strong>, so make sure DKIM, SPF, and (if used) DMARC
              are all valid and aligned.
            </li>
            <li>
              Keep an eye on complaint rates, bounces, blocks, drops, and
              spam‑trap hits; poor stats in any of these areas drag down your
              reputation and hurt inbox placement.
            </li>
            <li>
              Need help? Review AOL’s{" "}
              <a
                href="https://senders.yahooinc.com/faqs"
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
              , then use their{" "}
              <a
                href="https://senders.yahooinc.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact form
              </a>{" "}
              to open a case and resolve issues.
            </li>
          </ul>

          {/* <h4>Learn More</h4>
          <p>
            AOL official best practices via InboxDoctor’s Best Practices Guide
            for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },

  {
    type: "hit-spam-folder",
    provider: EmailProvider.GMX,
    title: "Your email landed in the GMX Spam folder",
    iconUrl: "/providers-logos/gmx.png",
    links: [
      // {
      //   label: "How to Avoid the GMX Spam Folder with InboxDoctor",
      //   href: "#",
      // },
      {
        label: "Avoid Spam Classification",
        href: "https://support.gmx.com/email/spam-and-viruses/avoid-spam-classification.html",
      },
      {
        label: "Contact GMX",
        href: "https://support.gmx.com/contact-us/contact-us.html",
      },
    ],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            When an email lands in the GMX spam folder, it means GMX’s filtering
            system—aligned with standards from the Messaging Anti‑Abuse Working
            Group (M3AAWG) and Certified Senders Alliance (CSA)—has flagged the
            message as potentially unwanted. Triggers include poor sender
            reputation, missing authentication, high bounce rates, or policy
            violations (e.g., absent DKIM, unverified recipients). This limits
            inbox visibility, hurting engagement and campaign performance.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Check Authentication</h4>
          <ul>
            <li>
              Ensure <strong>SPF, DKIM, and DMARC</strong> are correctly
              configured (use InboxDoctor’s <em>Inbox Placement Test Report</em>
              ). GMX
              <em>requires</em> valid DKIM with domain alignment and recommends
              SPF &amp; DMARC to block spoofing and boost trust.
            </li>
          </ul>

          <h4>Verify Your Email Server Setup</h4>
          <ul>
            <li>
              Send from a <strong>static IP</strong>; dynamic IPs are blocked.
            </li>
            <li>
              Configure reverse DNS (<strong>rDNS</strong>) so it resolves to
              your domain, e.g., <code>mail.yourdomain.tld</code>—avoid default
              ISP labels such as{" "}
              <code>123-123-123-123-static.yourprovider.tld</code>.
            </li>
          </ul>

          <h4>Verify IP or Domain Blocklist Status</h4>
          <ul>
            <li>
              Use InboxDoctor to check whether your IP or domain is
              block‑listed, which would affect GMX deliverability.
            </li>
          </ul>

          {/* <h4>Learn More</h4>
          <p>
            GMX official best practices via InboxDoctor’s Best Practices Guide
            for guidance on avoiding filters.
          </p> */}
        </div>
      );
    },
  },
  {
    type: "hit-spam-folder",
    provider: EmailProvider.ICLOUD,
    title: "Your email landed in the iCloud Spam folder.",
    iconUrl: "/providers-logos/icloud.png",
    links: [],
    content: () => {
      return (
        <div className="space-y-2">
          <p>
            iCloud Mail uses advanced filters to protect users from unwanted
            emails (spam, phishing). Legit messages can be flagged when the
            sender lacks proper authentication (SPF, DKIM, DMARC), has a poor
            reputation, or the content triggers spam filters. Additional risk
            factors include sending to unengaged recipients or using free email
            domains, which Apple trusts less.
          </p>

          <h3>Troubleshooting Tips</h3>

          <h4>Ensure Authentication</h4>
          <ul>
            <li>
              Verify <strong>SPF, DKIM, and DMARC</strong> with InboxDoctor’s{" "}
              <em>Inbox Placement Test Report</em>; iCloud places strong weight
              on correct authentication.
            </li>
          </ul>

          <h4>Check for Blacklisted IPs</h4>
          <ul>
            <li>
              If your sending IP is blacklisted by Proofpoint, Apple Mail will
              block it. Use InboxDoctor to confirm whether your IP or domain is
              on a blocklist that could harm iCloud deliverability.
            </li>
          </ul>

          <h4>Report Issues</h4>
          <ul>
            <li>
              Submit delivery problems directly to Apple via{" "}
              <a
                href="https://support.apple.com/icloud"
                target="_blank"
                rel="noopener noreferrer"
              >
                iCloud Support
              </a>
              .
            </li>
          </ul>
        </div>
      );
    },
  },
];
